import { useState, useEffect, useMemo, useRef } from 'react';
import type { NostrEvent } from '@nostrify/nostrify';
import { getTagValue, type PageEntry, pageSlugFromDTag } from '@/lib/stele';
import { Search, X, FileText } from 'lucide-react';

interface ReaderSearchProps {
  pages: NostrEvent[];
  pageEntries: PageEntry[];
  pageTitles: Map<string, string>;
  onClose: () => void;
  onNavigate: (path: string) => void;
}

interface SearchResult {
  dTag: string;
  title: string;
  snippet: string;
  path: string;
}

export function ReaderSearch({ pages, pageEntries, pageTitles, onClose, onNavigate }: ReaderSearchProps) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const results = useMemo<SearchResult[]>(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();

    return pages.flatMap(page => {
      const dTag    = getTagValue(page.tags, 'd');
      if (!dTag) return [];
      const title   = getTagValue(page.tags, 'title') ?? '';
      const content = page.content ?? '';
      const entry   = pageEntries.find(e => e.dTag === dTag);
      if (!entry) return [];

      const titleMatch   = title.toLowerCase().includes(q);
      const contentMatch = content.toLowerCase().includes(q);
      if (!titleMatch && !contentMatch) return [];

      let snippet = '';
      if (contentMatch) {
        const idx   = content.toLowerCase().indexOf(q);
        const start = Math.max(0, idx - 60);
        const end   = Math.min(content.length, idx + q.length + 60);
        snippet = (start > 0 ? '…' : '') + content.slice(start, end).replace(/\n/g, ' ') + (end < content.length ? '…' : '');
      }

      return [{
        dTag,
        title: title || pageSlugFromDTag(dTag) || 'Untitled',
        snippet,
        path: entry.path,
      }];
    }).slice(0, 8);
  }, [query, pages, pageEntries]);

  useEffect(() => { setSelectedIndex(0); }, [query]);

  useEffect(() => {
    const fn = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(i => Math.min(i + 1, results.length - 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setSelectedIndex(i => Math.max(i - 1, 0)); }
      if (e.key === 'Enter' && results[selectedIndex]) onNavigate(results[selectedIndex].path);
    };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, [results, selectedIndex, onClose, onNavigate]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg mx-4 bg-card border border-border rounded-sm overflow-hidden shadow-2xl">

        {/* Input */}
        <div className="flex items-center gap-3 px-4 border-b border-border">
          <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search pages…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="flex-1 h-12 bg-transparent text-sm outline-none placeholder:text-muted-foreground/40 font-mono"
          />
          <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground transition-colors">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-80 overflow-y-auto">
          {query && results.length === 0 && (
            <div className="py-10 text-center text-xs font-mono tracking-widest uppercase text-muted-foreground/40">
              No results
            </div>
          )}
          {results.map((result, i) => (
            <button
              key={result.dTag}
              className={`flex w-full items-start gap-3 px-4 py-3 text-left text-sm transition-colors border-b border-border last:border-0 ${
                i === selectedIndex ? 'bg-accent' : 'hover:bg-accent/50'
              }`}
              onClick={() => onNavigate(result.path)}
              onMouseEnter={() => setSelectedIndex(i)}
            >
              <FileText className="h-3.5 w-3.5 shrink-0 mt-0.5 text-muted-foreground" />
              <div className="min-w-0">
                <div className="font-medium text-foreground">{result.title}</div>
                {result.snippet && (
                  <p className="text-[11px] font-mono text-muted-foreground/60 mt-1 line-clamp-1">{result.snippet}</p>
                )}
              </div>
            </button>
          ))}
          {!query && (
            <div className="py-10 text-center text-xs font-mono tracking-widest uppercase text-muted-foreground/30">
              Type to search
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
